import {
	Box,
	Button,
	Chip,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { AutoAwesome, DownloadRounded, UploadRounded } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import axios from "axios";
import ExcelJS from "exceljs";
import { useEffect, useMemo, useRef, useState } from "react";
import RenderInputField from "components/PimDashboard/CommonComponents/RenderInputField/RenderInputField";
import { arrType, datatypes, inputTypes, selectOptionsKeyName } from "data/globalData";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import { channelTypes, regions, wearHouses } from "../../../BuyingSheet/data";
import { productionStageOptions } from "components/PimDashboard/data";
import { BUY_QTY_SHEET_API, parseStyleSizes } from "../../data";

const BUY_LEVEL_OPTIONS = [
	{ name: "Channel Level", key: "CHANNEL" },
	{ name: "Region Level", key: "REGION" },
	// { name: "Warehouse Level", key: "WAREHOUSE" },
];

const TABLE_PANEL_HEIGHT = 360;
const EXCEL_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const normalizeText = (value) => String(value ?? "").trim();
const normalizeHeader = (value) =>
	normalizeText(value)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "");

const safeNumber = (value) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const downloadWorkbook = async (workbook, fileName) => {
	const buffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([buffer], { type: EXCEL_MIME_TYPE });
	const link = document.createElement("a");
	link.href = URL.createObjectURL(blob);
	link.download = fileName;
	link.click();
	URL.revokeObjectURL(link.href);
};

const uniqueByKey = (list = []) => {
	const seen = new Set();
	return list.filter((item) => {
		const key = normalizeText(item?.key);
		if (!key) return false;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

const mergeOptionLists = (...lists) => {
	const map = new Map();
	lists.flat().forEach((item) => {
		const key = normalizeText(item?.key);
		if (!key) return;
		if (!map.has(key)) map.set(key, { name: item?.name ?? key, key });
	});
	return [...map.values()];
};

const buildSelectColumn = (name, key, list, inputType = inputTypes.SELECT.key) => ({
	name,
	key,
	input_type: inputType,
	data_type: datatypes.STRING.key,
	constraints: {},
	[selectOptionsKeyName]: { type: arrType, list },
});

const getExcelHeaderIndex = (headers = [], aliases = []) => headers.findIndex((header) => aliases.includes(normalizeHeader(header)));

const getRowKey = (row = {}) =>
	[normalizeText(row.channel), normalizeText(row.region), normalizeText(row.warehouse), normalizeText(row.size)].join("__");

const normalizeRow = (row = {}, parent = {}, fallbackIndex = 0) => {
	const channel = normalizeText(row.channel ?? row.channel_type ?? parent.channel ?? parent.channel_type ?? "");
	const region = normalizeText(row.region ?? row.region_name ?? parent.region ?? parent.region_name ?? "");
	const warehouse = normalizeText(row.warehouse ?? row.owner_site ?? parent.warehouse ?? parent.owner_site ?? "");
	const size = normalizeText(row.size ?? row.size_name ?? row.size_code ?? parent.size ?? "");
	const totalQty = safeNumber(row.total_qty ?? row.qty ?? row.warehouse_qty ?? row.region_total_qty ?? 0);
	const rowId =
		row.id ??
		row.buying_wh_qty_id ??
		row.channel_level_buying_id ??
		row.region_level_buying_id ??
		row.warehouse_level_buying_id ??
		row.__rowId ??
		`${channel || "channel"}-${region || "region"}-${warehouse || "warehouse"}-${size || "size"}-${fallbackIndex}`;

	return {
		...row,
		__rowId: rowId,
		__rowKey: getRowKey({ channel, region, warehouse, size }),
		id: row.id ?? null,
		channel,
		region,
		warehouse,
		size,
		total_qty: totalQty,
		is_new: !!row.is_new,
	};
};

const normalizeRowsFromApi = (rows = []) => rows.map((row, index) => normalizeRow(row, {}, index));

const cartesianProduct = (channels, regions, warehouses, sizes) => {
	const combos = [];
	channels.forEach((channel) => {
		regions.forEach((region) => {
			warehouses.forEach((warehouse) => {
				sizes.forEach((size) => {
					combos.push({ channel, region, warehouse, size });
				});
			});
		});
	});
	return combos;
};

const distributeQtyEvenly = (rows, totalQty) => {
	const total = Math.max(0, Math.floor(safeNumber(totalQty)));
	const count = rows.length;
	if (!count) return [];

	const base = Math.floor(total / count);
	let remainder = total % count;

	return rows.map((row) => {
		const qty = base + (remainder > 0 ? 1 : 0);
		remainder -= 1;
		return {
			...row,
			total_qty: qty,
		};
	});
};

const normalizeSizeKey = (value) => normalizeText(value).toUpperCase();

const buildFullRowsFromSelection = (selection, prevRows = [], sizeList = []) => {
	const channels = uniqueByKey((selection.channel ?? []).map((item) => ({ name: item, key: item })));
	const regions =
		selection.buy_level === "CHANNEL"
			? [{ name: "ALL", key: "ALL" }]
			: uniqueByKey((selection.region ?? []).map((item) => ({ name: item, key: item })));
	const warehouses = uniqueByKey((selection.warehouse ?? []).map((item) => ({ name: item, key: item })));
	const sizes = sizeList.map((size) => ({ name: size, key: size }));

	if (!channels.length || (selection.buy_level !== "CHANNEL" && !regions.length) || !warehouses.length || !sizes.length) {
		return [];
	}

	const prevMap = new Map(prevRows.map((row) => [row.__rowKey ?? getRowKey(row), row]));
	const combos = cartesianProduct(
		channels.map((row) => row.key),
		regions.map((row) => row.key),
		warehouses.map((row) => row.key),
		sizes.map((row) => row.key),
	);

	let nextRows = combos.map((combo) => {
		const key = [combo.channel, combo.region, combo.warehouse, combo.size].map(normalizeText).join("__");
		const prev = prevMap.get(key);
		return {
			__rowKey: key,
			channel: combo.channel,
			region: combo.region,
			warehouse: combo.warehouse,
			size: combo.size,
			id: prev?.id ?? null,
			is_new: prev?.is_new ?? !prev?.id,
			created_by: prev?.created_by ?? "",
			last_updated_by: prev?.last_updated_by ?? "",
			total_qty: safeNumber(prev?.total_qty ?? 0),
		};
	});

	const previousTotal = prevRows.reduce((acc, row) => acc + safeNumber(row.total_qty), 0);
	const preservedTotal = nextRows.reduce((acc, row) => acc + safeNumber(prevMap.get(row.__rowKey)?.total_qty ?? 0), 0);
	const leftover = Math.max(0, Math.floor(previousTotal - preservedTotal));

	if (leftover > 0 && nextRows.length) {
		const distributed = distributeQtyEvenly(
			nextRows.map((row) => ({ ...row, total_qty: 0 })),
			leftover,
		);
		const adjustmentMap = new Map(distributed.map((row) => [row.__rowKey, safeNumber(row.total_qty)]));
		nextRows = nextRows.map((row) => ({
			...row,
			total_qty: safeNumber(row.total_qty) + safeNumber(adjustmentMap.get(row.__rowKey) ?? 0),
		}));
	}

	return nextRows;
};

const expandLegacyRows = (rows = [], sizeList = []) => {
	const output = [];
	const grouped = new Map();

	rows.forEach((row, index) => {
		const normalized = normalizeRow(row, {}, index);
		if (normalized.size) {
			output.push(normalized);
			return;
		}

		const key = [normalized.channel, normalized.region, normalized.warehouse].map(normalizeText).join("__");
		const current = grouped.get(key) ?? { ...normalized, total_qty: 0 };
		current.total_qty += safeNumber(normalized.total_qty);
		grouped.set(key, current);
	});

	grouped.forEach((row, key) => {
		const sizeRows = sizeList.map((size) => ({
			...row,
			size,
			__rowKey: `${key}__${size}`,
		}));
		output.push(...distributeQtyEvenly(sizeRows, row.total_qty).map((item) => ({ ...item, __rowKey: getRowKey(item) })));
	});

	return output.map((row) => ({ ...row, __rowKey: row.__rowKey ?? getRowKey(row) }));
};

const getColorSummaryFields = (colorRow, sizeList = []) => [
	{ label: "Option ID", value: colorRow?.option_id },
	{ label: "Style ID", value: colorRow?.style_id },
	{ label: "Brand", value: colorRow?.brand },
	{ label: "Sub Brand", value: colorRow?.sub_brand },
	{ label: "Category", value: colorRow?.category },
	{ label: "Sub Category", value: colorRow?.sub_category },
	{ label: "Color", value: colorRow?.color },
	{ label: "Palette", value: colorRow?.palette ?? colorRow?.pallete },
	{ label: "Division", value: colorRow?.division },
	{ label: "Season", value: colorRow?.season },
	{ label: "Sizes", value: sizeList.length ? sizeList.join(", ") : "-" },
];

const toSelectionArray = (value) => {
	if (Array.isArray(value)) return value.map(normalizeText).filter(Boolean);
	const normalized = normalizeText(value);
	return normalized ? [normalized] : [];
};

const EditDetailsContent = ({
	modal,
	selectedColor,
	saveCallback,
	onCancelEdit = () => {},
	onExitEdit = () => {},
	onEnterEdit = () => {},
	onBusyChange,
	isEditMode = true,
	onRegisterActions = () => {},
}) => {
	const { user } = AuthState();
	const { setLoadingOpen, setSnackBarOpen, setSnackBar, snackBar } = GlobalState();

	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [sourceData, setSourceData] = useState({});
	const [fullRows, setFullRows] = useState([]);
	const importFileRef = useRef(null);
	const [selection, setSelection] = useState({
		buy_level: "CHANNEL",
		channel: [],
		region: ["ALL"],
		warehouse: [],
	});

	const styleSizes = useMemo(
		() => parseStyleSizes(sourceData?.sizes ?? selectedColor?.sizes),
		[sourceData?.sizes, selectedColor?.sizes],
	);

	const colorSnapshot = useMemo(() => ({ ...selectedColor, ...sourceData }), [selectedColor, sourceData]);
	const colorImageUrl = useMemo(() => normalizeText(colorSnapshot?.url), [colorSnapshot?.url]);
	const colorName = useMemo(() => normalizeText(colorSnapshot?.color), [colorSnapshot?.color]);
	const styleName = useMemo(() => normalizeText(colorSnapshot?.style_name), [colorSnapshot?.style_name]);
	const colorPalette = useMemo(
		() => normalizeText(colorSnapshot?.palette ?? colorSnapshot?.pallete),
		[colorSnapshot?.palette, colorSnapshot?.pallete],
	);
	const styleFields = useMemo(
		() => getColorSummaryFields(colorSnapshot, styleSizes).filter((field) => !["Color", "Palette"].includes(field.label)),
		[colorSnapshot, styleSizes],
	);
	const selectedOptionId = selectedColor?.option_id ?? null;
	const canEdit = isEditMode;
	const modeLabel = canEdit ? "Edit mode" : "View mode";

	const selectionFieldDisabled = !canEdit;
	const regionDisabled = selectionFieldDisabled || selection.buy_level === "CHANNEL";

	const channelOptions = useMemo(
		() =>
			uniqueByKey(
				mergeOptionLists(
					Object.values(channelTypes),
					selection.channel.map((value) => ({ name: value, key: value })),
				),
			),
		[selection.channel],
	);
	const regionOptions = useMemo(() => {
		if (selection.buy_level === "CHANNEL") return [{ name: "ALL", key: "ALL" }];

		return uniqueByKey(
			mergeOptionLists(
				Object.values(regions),
				selection.region.map((value) => ({ name: value, key: value })),
			),
		);
	}, [selection.buy_level, selection.region]);
	const warehouseOptions = useMemo(
		() =>
			uniqueByKey(
				mergeOptionLists(
					Object.values(wearHouses),
					selection.warehouse.map((value) => ({ name: value, key: value })),
				),
			),
		[selection.warehouse],
	);

	const buyLevelColumn = useMemo(() => buildSelectColumn("Buy Level", "buy_level", BUY_LEVEL_OPTIONS), []);
	const channelColumn = useMemo(
		() => buildSelectColumn("Channel", "channel", channelOptions, inputTypes.MULTIPLE_SELECT.key),
		[channelOptions],
	);
	const regionColumn = useMemo(
		() => buildSelectColumn("Region", "region", regionOptions, inputTypes.MULTIPLE_SELECT.key),
		[regionOptions],
	);
	const warehouseColumn = useMemo(
		() => buildSelectColumn("Warehouse", "warehouse", warehouseOptions, inputTypes.MULTIPLE_SELECT.key),
		[warehouseOptions],
	);

	const syncRowsForSelection = (nextSelection) => {
		setFullRows((currentRows) => buildFullRowsFromSelection(nextSelection, currentRows, styleSizes));
	};

	const updateSelection = (updater) => {
		setSelection((prev) => {
			const next = typeof updater === "function" ? updater(prev) : updater;
			syncRowsForSelection(next);
			return next;
		});
	};

	useEffect(() => {
		let alive = true;

		const loadRows = async () => {
			if (!modal) return;

			setLoading(true);
			setError("");

			try {
				let nextData = { ...(selectedColor ?? {}) };
				let apiRows = [];

				if (selectedOptionId != null) {
					const response = await axios.get(`${BUY_QTY_SHEET_API}/get_buy_qty/${selectedOptionId}`);
					const result = response?.data?.result ?? {};
					nextData = { ...nextData, ...(result.header ?? {}), ...(result.color ?? {}), sizes: result.sizes };
					apiRows = result.rows ?? [];
				}

				const styleSizesFromApi = parseStyleSizes(nextData?.sizes ?? selectedColor?.sizes);
				const nextRows = normalizeRowsFromApi(apiRows);
				const fullRowsFromSource = expandLegacyRows(nextRows, styleSizesFromApi);
				const firstRow = fullRowsFromSource[0] ?? nextRows[0] ?? {};
				const headerChannels = nextData?.channels ?? nextData?.channel;
				const headerRegions = nextData?.regions ?? nextData?.region;
				const headerWarehouses = nextData?.warehouses ?? nextData?.warehouse;
				const nextBuyLevel = nextData?.buy_level ?? "CHANNEL";
				const nextSelection = {
					buy_level: nextBuyLevel,
					channel: toSelectionArray(headerChannels ?? firstRow.channel ?? ""),
					region: nextBuyLevel === "CHANNEL" ? ["ALL"] : toSelectionArray(headerRegions ?? firstRow.region ?? ""),
					warehouse: toSelectionArray(headerWarehouses ?? firstRow.warehouse ?? ""),
				};

				if (!alive) return;

				setSourceData({ ...(nextData ?? {}), sizes: styleSizesFromApi });
				setFullRows(
					fullRowsFromSource.length ? fullRowsFromSource : buildFullRowsFromSelection(nextSelection, [], styleSizesFromApi),
				);
				setSelection(nextSelection);
			} catch (loadError) {
				if (!alive) return;
				setError(parseErrorMessage(loadError));
				setSourceData({ ...(selectedColor ?? {}), sizes: parseStyleSizes(selectedColor?.sizes) });
				const fallbackRows = expandLegacyRows(normalizeRowsFromApi([]), parseStyleSizes(selectedColor?.sizes));
				setFullRows(fallbackRows);
			} finally {
				if (alive) setLoading(false);
			}
		};

		loadRows();

		return () => {
			alive = false;
		};
	}, [modal, selectedOptionId, selectedColor]);

	const totalQty = useMemo(() => fullRows.reduce((acc, row) => acc + safeNumber(row.total_qty), 0), [fullRows]);
	const canPublish = useMemo(() => {
		const stage = String(colorSnapshot?.stage ?? "")
			.trim()
			.toUpperCase();
		return stage === productionStageOptions.BUYING.key && totalQty > 0;
	}, [colorSnapshot?.stage, totalQty]);

	const setSelectionValue = (key, value) => {
		if (key === "buy_level") {
			updateSelection((prev) => ({
				...prev,
				buy_level: value,
				region: value === "CHANNEL" ? ["ALL"] : prev.region?.filter((item) => item !== "ALL"),
			}));
			return;
		}

		updateSelection((prev) => ({
			...prev,
			[key]: Array.isArray(value) ? value : value ? [value] : [],
			...(prev.buy_level === "CHANNEL" && key === "region" ? { region: ["ALL"] } : null),
		}));
	};

	const handleFullQtyChange = (rowKey, qty) => {
		setFullRows((prev) =>
			prev.map((row) => {
				if (row.__rowKey !== rowKey) return row;
				return {
					...row,
					total_qty: Math.max(0, Math.floor(safeNumber(qty))),
				};
			}),
		);
	};

	const handleDownloadExcel = async () => {
		try {
			const workbook = new ExcelJS.Workbook();
			const worksheet = workbook.addWorksheet("Full Rows");
			const headers = ["Channel", "Region", "Warehouse", "Size", "Qty"];

			worksheet.addRow(headers);
			fullRows.forEach((row) => {
				worksheet.addRow([
					row.channel || "",
					row.region || "",
					row.warehouse || "",
					row.size || "",
					safeNumber(row.total_qty),
				]);
			});

			worksheet.getRow(1).font = { bold: true };
			worksheet.columns = headers.map((header) => ({ header, width: Math.max(14, header.length + 4) }));
			await downloadWorkbook(workbook, `buy_qty_full_rows_${selectedColor?.option_id ?? "template"}.xlsx`);
		} catch (downloadError) {
			const message = parseErrorMessage(downloadError);
			setError(message);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	const applyImportedRows = (importedRows) => {
		const importedMap = new Map(importedRows.map((row) => [row.__groupKey, row]));
		setFullRows((prev) =>
			prev.map((row) => {
				const imported = importedMap.get(getRowKey(row));
				if (!imported) return row;
				return {
					...row,
					channel: imported.channel,
					region: imported.region,
					warehouse: imported.warehouse,
					size: imported.size,
					total_qty: safeNumber(imported.total_qty),
				};
			}),
		);
	};

	const validateImportedRows = (rows, allowedSizes = []) => {
		const currentKeys = new Set(fullRows.map((row) => getRowKey(row)));
		const importedKeys = new Set();
		const issues = [];
		const importedRows = [];
		const allowedSizeKeys = new Set(allowedSizes.map((size) => normalizeSizeKey(size)));

		rows.forEach((row, index) => {
			const channel = normalizeText(row.channel);
			const region = normalizeText(row.region);
			const warehouse = normalizeText(row.warehouse);
			const size = normalizeText(row.size).toUpperCase();
			const qty = safeNumber(row.total_qty);
			const rowNumber = index + 2;

			if (!channel) issues.push(`Row ${rowNumber}: Channel is required.`);
			if (!region) issues.push(`Row ${rowNumber}: Region is required.`);
			if (!warehouse) issues.push(`Row ${rowNumber}: Warehouse is required.`);
			if (!size) issues.push(`Row ${rowNumber}: Size is required.`);
			if (!Number.isFinite(Number(row.total_qty)) || qty < 0)
				issues.push(`Row ${rowNumber}: Qty must be a non-negative number.`);

			if (channel && !Object.prototype.hasOwnProperty.call(channelTypes, channel)) {
				issues.push(`Row ${rowNumber}: Unknown channel "${channel}".`);
			}
			if (region && !Object.prototype.hasOwnProperty.call(regions, region) && region !== "ALL") {
				issues.push(`Row ${rowNumber}: Unknown region "${region}".`);
			}
			if (warehouse && !Object.prototype.hasOwnProperty.call(wearHouses, warehouse)) {
				issues.push(`Row ${rowNumber}: Unknown warehouse "${warehouse}".`);
			}
			if (size && allowedSizes.length && !allowedSizeKeys.has(normalizeSizeKey(size))) {
				issues.push(`Row ${rowNumber}: Invalid size "${size}".`);
			}
			if (!allowedSizes.length) {
				issues.push("No sizes configured for this style in style_details.");
			}

			const key = getRowKey({ channel, region, warehouse, size });
			if (importedKeys.has(key)) {
				issues.push(`Row ${rowNumber}: Duplicate row for ${key}.`);
			}
			importedKeys.add(key);

			importedRows.push({
				__groupKey: key,
				channel,
				region,
				warehouse,
				size,
				total_qty: qty,
			});
		});

		const missingKeys = [...currentKeys].filter((key) => !importedKeys.has(key));
		const extraKeys = [...importedKeys].filter((key) => !currentKeys.has(key));

		if (missingKeys.length) {
			issues.push(`Missing ${missingKeys.length} expected row(s) from the current selection.`);
		}
		if (extraKeys.length) {
			issues.push(`Found ${extraKeys.length} row(s) that do not match the current selection.`);
		}

		if (issues.length) {
			throw new Error(issues.slice(0, 12).join(" "));
		}

		return importedRows;
	};

	const handleImportExcel = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";

		if (!file) return;

		const fileName = file.name.toLowerCase();
		if (!fileName.endsWith(".xlsx")) {
			const message = "Please upload a valid .xlsx Excel file.";
			setError(message);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		setLoading(true);
		setError("");

		try {
			const workbook = new ExcelJS.Workbook();
			await workbook.xlsx.load(await file.arrayBuffer());
			const worksheet = workbook.worksheets[0];

			if (!worksheet) {
				throw new Error("The selected Excel file does not contain any sheets.");
			}

			const headers = worksheet
				.getRow(1)
				.values.slice(1)
				.map((value) => normalizeText(value));
			if (!headers.length) {
				throw new Error("The first sheet must contain a header row.");
			}

			const channelIndex = getExcelHeaderIndex(headers, ["channel"]);
			const regionIndex = getExcelHeaderIndex(headers, ["region"]);
			const warehouseIndex = getExcelHeaderIndex(headers, ["warehouse", "wh"]);
			const sizeIndex = getExcelHeaderIndex(headers, ["size"]);
			const qtyIndex = getExcelHeaderIndex(headers, ["qty", "quantity", "totalqty", "total_qty"]);

			if (channelIndex < 0 || warehouseIndex < 0 || sizeIndex < 0 || qtyIndex < 0) {
				throw new Error("Excel file must include Channel, Warehouse, Size, and Qty columns.");
			}

			if (selection.buy_level !== "CHANNEL" && regionIndex < 0) {
				throw new Error("Excel file must include a Region column for the current buy level.");
			}

			const parsedRows = [];
			worksheet.eachRow((row, rowNumber) => {
				if (rowNumber === 1) return;

				const values = row.values.slice(1);
				const hasValue = values.some((value) => normalizeText(value));
				if (!hasValue) return;

				parsedRows.push({
					channel: values[channelIndex],
					region: regionIndex >= 0 ? values[regionIndex] : "ALL",
					warehouse: values[warehouseIndex],
					size: values[sizeIndex],
					total_qty: values[qtyIndex],
				});
			});

			const importedRows = validateImportedRows(parsedRows, styleSizes);
			applyImportedRows(importedRows);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Imported ${importedRows.length} full row(s) successfully.`,
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} catch (importError) {
			const message = parseErrorMessage(importError);
			setError(message);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoading(false);
		}
	};

	const handleSaveClick = async (publish = false) => {
		setSaving(true);
		setLoadingOpen(true);
		setError("");

		try {
			if (publish && !canPublish) {
				const stage = String(colorSnapshot?.stage ?? "")
					.trim()
					.toUpperCase();
				throw new Error(
					stage !== productionStageOptions.BUYING.key
						? `Color must be in ${productionStageOptions.BUYING.key} stage to publish buy qty`
						: "Total qty must be greater than 0 to publish buy qty",
				);
			}

			const payload = {
				option_id: selectedColor?.option_id ?? selectedOptionId,
				buy_level: selection.buy_level,
				channels: selection.channel,
				regions: selection.region,
				warehouses: selection.warehouse,
				publish,
				rows: fullRows.map((row) => ({
					channel: row.channel,
					region: row.region,
					warehouse: row.warehouse,
					size: row.size,
					total_qty: safeNumber(row.total_qty),
				})),
			};

			const response = await axios.post(`${BUY_QTY_SHEET_API}/save_buy_qty`, payload);
			const resultData = response?.data?.result ?? response?.data ?? payload;
			const mergedData = {
				...selectedColor,
				...(resultData.header ?? {}),
				...(resultData.color ?? {}),
				sizes: parseStyleSizes(resultData.sizes ?? sourceData?.sizes),
			};

			if (typeof saveCallback === "function") {
				saveCallback(mergedData, { publish });
			}

			setSourceData(mergedData);
			const savedSizes = parseStyleSizes(resultData.sizes ?? mergedData.sizes);
			setFullRows(expandLegacyRows(normalizeRowsFromApi(resultData.rows ?? []), savedSizes));
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: publish ? "Buy qty saved and published successfully" : "Buy qty saved successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			onExitEdit();
		} catch (saveError) {
			setError(parseErrorMessage(saveError));
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: parseErrorMessage(saveError),
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setSaving(false);
			setLoadingOpen(false);
		}
	};

	const handleResetEdit = () => {
		const resetSizes = parseStyleSizes(sourceData?.sizes ?? selectedColor?.sizes);
		const sourceRows = expandLegacyRows(normalizeRowsFromApi(sourceData?.rows ?? []), resetSizes);
		setFullRows(
			sourceRows.length
				? sourceRows
				: buildFullRowsFromSelection(
						{
							buy_level: sourceData?.buy_level ?? "CHANNEL",
							channel: toSelectionArray(sourceData?.channels ?? sourceData?.channel ?? ""),
							region:
								(sourceData?.buy_level ?? "CHANNEL") === "CHANNEL"
									? ["ALL"]
									: toSelectionArray(sourceData?.regions ?? sourceData?.region ?? ""),
							warehouse: toSelectionArray(sourceData?.warehouses ?? sourceData?.warehouse ?? ""),
						},
						[],
						resetSizes,
					),
		);
		const firstRow = sourceRows[0] ?? {};
		setSelection({
			buy_level: sourceData?.buy_level ?? "CHANNEL",
			channel: toSelectionArray(sourceData?.channels ?? sourceData?.channel ?? firstRow.channel ?? ""),
			region:
				(sourceData?.buy_level ?? "CHANNEL") === "CHANNEL"
					? ["ALL"]
					: toSelectionArray(sourceData?.regions ?? sourceData?.region ?? firstRow.region ?? ""),
			warehouse: toSelectionArray(sourceData?.warehouses ?? sourceData?.warehouse ?? firstRow.warehouse ?? ""),
		});
		onCancelEdit();
	};

	const loadingOverlay = loading || saving;

	useEffect(() => {
		if (typeof onRegisterActions === "function") {
			onRegisterActions({
				publish: () => handleSaveClick(true),
				canPublish,
			});
		}
	}, [canPublish, handleSaveClick, onRegisterActions]);

	useEffect(() => {
		if (typeof onBusyChange === "function") {
			onBusyChange(loadingOverlay);
		}
	}, [loadingOverlay, onBusyChange]);

	useEffect(
		() => () => {
			if (typeof onBusyChange === "function") {
				onBusyChange(false);
			}
		},
		[onBusyChange],
	);

	return (
		<Stack
			spacing={1.5}
			sx={{
				flex: 1,
				overflow: "hidden",
				pt: 1,
				height: "93%",
			}}>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "1fr", lg: "320px minmax(0, 1fr)" },
					gap: 1.5,
					alignItems: "start",
					height: "100%",
					boxSizing: "border-box",
				}}>
				<Box
					sx={{
						border: `1px solid ${grey[200]}`,
						borderRadius: 2,
						p: 1.5,
						bgcolor: grey[50],
						position: { lg: "sticky" },
						top: { lg: 12 },
						height: "100%",
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
						boxSizing: "border-box",
					}}>
					<Box
						sx={{
							display: "flex",
							gap: 1.25,
							mb: 1.25,
							flexShrink: 0,
							alignItems: "stretch",
							minHeight: 140,
						}}>
						<Box
							sx={{
								width: 130,
								flexShrink: 0,
								alignSelf: "stretch",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								bgcolor: "#fff",
								borderRadius: 2,
								border: `1px solid ${grey[100]}`,
								overflow: "hidden",
							}}>
							{colorImageUrl ? (
								<Box
									component="img"
									src={colorImageUrl}
									alt={colorName ? `${colorName} swatch` : "Color image"}
									sx={{
										display: "block",
										maxWidth: "100%",
										maxHeight: "100%",
										width: "auto",
										height: "auto",
										objectFit: "contain",
										objectPosition: "center",
									}}
								/>
							) : (
								<Typography variant="body2" color={grey[500]} sx={{ px: 1, py: 2, textAlign: "center", fontSize: 12 }}>
									No image
								</Typography>
							)}
						</Box>

						<Box
							sx={{
								flex: 1,
								minWidth: 0,
								display: "flex",
								flexDirection: "column",
								gap: 1,
								alignSelf: "stretch",
							}}>
							<Box
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									bgcolor: "#fff",
									borderRadius: 1.25,
									px: 1.25,
									py: 0.9,
									border: `1px solid ${grey[100]}`,
								}}>
								<Typography variant="caption" color={grey[600]} sx={{ lineHeight: 1.2 }}>
									Style Name
								</Typography>
								<Typography
									variant="body2"
									fontWeight={700}
									sx={{ wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal" }}>
									{styleName || "-"}
								</Typography>
							</Box>
							<Box
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									bgcolor: "#fff",
									borderRadius: 1.25,
									px: 1.25,
									py: 0.9,
									border: `1px solid ${grey[100]}`,
								}}>
								<Typography variant="caption" color={grey[600]} sx={{ lineHeight: 1.2 }}>
									Color
								</Typography>
								<Typography
									variant="body2"
									fontWeight={700}
									sx={{ wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal" }}>
									{colorName || "-"}
								</Typography>
							</Box>
							<Box
								sx={{
									flex: 1,
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									bgcolor: "#fff",
									borderRadius: 1.25,
									px: 1.25,
									py: 0.9,
									border: `1px solid ${grey[100]}`,
								}}>
								<Typography variant="caption" color={grey[600]} sx={{ lineHeight: 1.2 }}>
									Palette
								</Typography>
								<Typography
									variant="body2"
									fontWeight={700}
									sx={{ wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal" }}>
									{colorPalette || "-"}
								</Typography>
							</Box>
						</Box>
					</Box>

					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							overflowY: "auto",
							overflowX: "hidden",
							pr: 0.5,
						}}>
						<Box display="grid" gridTemplateColumns="1fr" gap={1}>
							{styleFields.map((field) => (
								<Box
									key={field.label}
									sx={{
										display: "flex",
										flexDirection: "column",
										gap: 0.35,
										bgcolor: "#fff",
										borderRadius: 1.25,
										px: 1.25,
										py: 0.9,
										border: `1px solid ${grey[100]}`,
									}}>
									<Typography variant="caption" color={grey[600]} sx={{ lineHeight: 1.2 }}>
										{field.label}
									</Typography>
									<Typography
										variant="body2"
										fontWeight={700}
										sx={{
											wordBreak: "break-word",
											overflowWrap: "anywhere",
											whiteSpace: "normal",
										}}>
										{field.value ?? "-"}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						border: `1px solid ${grey[200]}`,
						borderRadius: 2,
						p: 1.5,
						bgcolor: grey[50],
						boxSizing: "border-box",
						height: "100%",
						overflow: "auto",
					}}>
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							p: 1.25,
							border: `1px solid ${grey[200]}`,
							borderRadius: 2,
							bgcolor: "white",
							gap: 1,
						}}>
						<RenderInputField
							column={buyLevelColumn}
							data={selection}
							setData={setSelection}
							selectOptObjName={selectOptionsKeyName}
							disabled={selectionFieldDisabled}
							onChangeCallBack={(_, value) => setSelectionValue("buy_level", value)}
							width="28%"
						/>
						<Box display="flex" gap={1} flexWrap="wrap" alignItems="center" flexGrow={1} justifyContent="flex-end">
							{canEdit ? (
								<>
									<Button
										variant="outlined"
										onClick={handleResetEdit}
										disabled={loadingOverlay}
										sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
										Cancel
									</Button>
									<Button
										variant="contained"
										color="inherit"
										onClick={() => handleSaveClick(false)}
										disabled={loadingOverlay}
										sx={{
											borderRadius: 2,
											textTransform: "none",
											fontWeight: 800,
											bgcolor: grey[900],
											color: "#fff",
											"&:hover": { bgcolor: grey[800] },
										}}>
										Save
									</Button>
									<Button
										variant="contained"
										color="success"
										onClick={() => handleSaveClick(true)}
										disabled={loadingOverlay}
										sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}>
										Save & Publish
									</Button>
								</>
							) : (
								<Button
									variant="contained"
									onClick={onEnterEdit}
									disabled={loadingOverlay}
									sx={{ borderRadius: 999, textTransform: "none", fontWeight: 800, minWidth: 110 }}>
									Edit
								</Button>
							)}
						</Box>
						<Box display="flex" columnGap={1} width="100%">
							<RenderInputField
								column={channelColumn}
								data={selection}
								setData={setSelection}
								selectOptObjName={selectOptionsKeyName}
								disabled={selectionFieldDisabled}
								onChangeCallBack={(_, value) => setSelectionValue("channel", value)}
								longWidth="28%"
							/>
							<RenderInputField
								column={regionColumn}
								data={selection}
								setData={setSelection}
								selectOptObjName={selectOptionsKeyName}
								disabled={regionDisabled}
								onChangeCallBack={(_, value) => setSelectionValue("region", value)}
								longWidth="28%"
							/>
							<RenderInputField
								column={warehouseColumn}
								data={selection}
								setData={setSelection}
								selectOptObjName={selectOptionsKeyName}
								disabled={selectionFieldDisabled}
								onChangeCallBack={(_, value) => setSelectionValue("warehouse", value)}
								longWidth="28%"
							/>
						</Box>
					</Box>

					<Box
						sx={{
							mt: 2,
							border: `1px solid ${grey[200]}`,
							borderRadius: 2,
							bgcolor: "white",
							overflow: "hidden",
						}}>
						<Box
							sx={{
								px: 1.25,
								pt: 1,
								pb: 1,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								flexWrap: "wrap",
								gap: 1,
							}}>
							<Box display="flex" alignItems="center" gap={1}>
								<Typography variant="h6" fontWeight={800}>
									Qty Rows
								</Typography>
								{/* <Button variant="contained" size="small" startIcon={<AutoAwesome />}>
									Suggest
								</Button> */}
							</Box>
							<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
								<Typography variant="body2" color={grey[600]}>
									Total qty across the selection: <strong>{totalQty}</strong>
								</Typography>
								<Button
									size="small"
									variant="outlined"
									startIcon={<DownloadRounded />}
									onClick={handleDownloadExcel}
									disabled={loadingOverlay}
									sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
									Excel
								</Button>
								<Button
									size="small"
									variant="contained"
									startIcon={<UploadRounded />}
									onClick={() => importFileRef.current?.click()}
									disabled={loadingOverlay}
									sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
									Import Excel
								</Button>
								<input ref={importFileRef} type="file" accept=".xlsx" hidden onChange={handleImportExcel} />
							</Box>
						</Box>
						<TableContainer
							component={Paper}
							variant="outlined"
							sx={{
								borderRadius: 2,
								overflow: "auto",
								minHeight: TABLE_PANEL_HEIGHT,
								maxHeight: TABLE_PANEL_HEIGHT,
								mx: 1,
								mb: 1,
							}}>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 700 }}>Channel</TableCell>
										<TableCell sx={{ fontWeight: 700 }} align="center">
											Region
										</TableCell>
										<TableCell sx={{ fontWeight: 700 }}>Warehouse</TableCell>
										<TableCell sx={{ fontWeight: 700 }}>Size</TableCell>
										<TableCell sx={{ fontWeight: 700 }} align="right">
											Qty
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{fullRows.length ? (
										fullRows.map((row) => (
											<TableRow key={row.__rowKey} hover>
												<TableCell>{row.channel || "-"}</TableCell>
												<TableCell align="center">{row.region || "ALL"}</TableCell>
												<TableCell>{row.warehouse || "-"}</TableCell>
												<TableCell>{row.size || "-"}</TableCell>
												<TableCell align="right">
													<TextField
														size="small"
														type="number"
														value={row.total_qty}
														onChange={(event) => handleFullQtyChange(row.__rowKey, event.target.value)}
														inputProps={{ min: 0, step: 1 }}
														sx={{ width: 120 }}
														disabled={!canEdit}
													/>
												</TableCell>
											</TableRow>
										))
									) : (
										<TableRow>
											<TableCell colSpan={5} align="center">
												No full rows are available for the current selection.
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>

					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 1,
							px: 0.5,
							pt: 0.5,
						}}>
						<Typography variant="body2" color={grey[700]}>
							Total qty across all rows: <strong>{totalQty}</strong>
						</Typography>
						<Typography variant="body2" color={grey[600]}>
							{saving
								? "Saving changes..."
								: loading
									? "Loading buy data..."
									: canEdit
										? "Save keeps it as draft. Save & Publish finalizes it."
										: "Click Edit to unlock changes."}
						</Typography>
					</Box>
				</Box>
			</Box>
		</Stack>
	);
};

export default EditDetailsContent;
